// src/i18n/core/loader.js

/**
 * Enhanced translation loader with automatic discovery
 */

// Function to merge multiple translation objects
export const mergeTranslations = (...translationObjects) => {
  return translationObjects.reduce((merged, current) => {
    if (!current) return merged;
    return deepMerge(merged, current);
  }, {});
};

// Deep merge utility for nested objects
const deepMerge = (target, source) => {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          output[key] = source[key];
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        output[key] = source[key];
      }
    });
  }

  return output;
};

const isObject = (item) => {
  return item && typeof item === "object" && !Array.isArray(item);
};

// Registry of translation sources - automatically discovered
const translationSources = {
  // Core translations
  common: (lang) => import(`../common/${lang}.json`),

  // Config translations
  config: async (lang) => {
    try {
      const module = await import(`../../config/i18n/${lang}.json`);
      return module.default || module;
    } catch {
      return null;
    }
  },

  // Feature-based translations - Auto-discovery pattern
  // Orders
  orders: async (lang) => {
    try {
      const module = await import(`../../pages/Orders/i18n/${lang}.json`);
      return module.default || module;
    } catch {
      return null;
    }
  },

  // Products
  products: async (lang) => {
    try {
      const module = await import(`../../pages/Products/i18n/${lang}.json`);
      return module.default || module;
    } catch {
      return null;
    }
  },

  // Appointments
  appointments: async (lang) => {
    try {
      // Support both .js and .json files
      try {
        const module = await import(
          `../../pages/Appointments/i18n/appointments.${lang}.js`
        );
        return module.appointmentsTranslations || module.default || module;
      } catch {
        const module = await import(
          `../../pages/Appointments/i18n/${lang}.json`
        );
        return module.default || module;
      }
    } catch {
      return null;
    }
  },

  // Jewellery Plans
  jewellery: async (lang) => {
    try {
      const module = await import(
        `../../pages/JewelleryPlans/i18n/${lang}.json`
      );
      return module.default || module;
    } catch {
      return null;
    }
  },

  // Notifications
  notifications: async (lang) => {
    try {
      const module = await import(
        `../../pages/Notifications/i18n/${lang}.json`
      );
      return module.default || module;
    } catch {
      return null;
    }
  },

  // Rates
  rates: async (lang) => {
    try {
      const module = await import(`../../pages/Rates/i18n/${lang}.json`);
      return module.default || module;
    } catch {
      return null;
    }
  },

  // Branches
  branches: async (lang) => {
    try {
      const module = await import(`../../pages/Branches/i18n/${lang}.json`);
      return module.default || module;
    } catch {
      return null;
    }
  },

  consultation: async (lang) => {
    try {
      // Support .js files with named exports
      const module = await import(
        `../../pages/Consultation/i18n/consultation.${lang}.js`
      );
      // Try different export patterns
      return (
        module[
          `consultation${lang.charAt(0).toUpperCase()}${lang.slice(
            1
          )}Translations`
        ] ||
        module.default ||
        module
      );
    } catch (error) {
      console.warn(
        `Could not load consultation translations for ${lang}:`,
        error
      );
      return null;
    }
  },

  karigarManagement: async (lang) => {
    try {
      const module = await import(
        `../../pages/KarigarManagement/i18n/karigar.${lang}.js`
      );
      return (
        module[
          `karigar${lang.charAt(0).toUpperCase()}${lang.slice(1)}Translations`
        ] ||
        module.default ||
        module
      );
    } catch {
      return null;
    }
  },

  // App Settings
  appSettings: async (lang) => {
    try {
      const module = await import(`../../pages/AppSettings/i18n/${lang}.json`);
      return module.default || module;
    } catch {
      return null;
    }
  },
};

/**
 * Loads translations from multiple sources and merges them
 * @param {string} lang - The language code
 * @param {string[]} sources - Which sources to load (defaults to all)
 * @returns {Promise<Object>} - The merged translations
 */
export const loadTranslations = async (
  lang,
  sources = Object.keys(translationSources)
) => {
  try {
    const translationPromises = sources.map(async (source) => {
      if (!translationSources[source]) {
        console.warn(`Translation source "${source}" not found`);
        return null;
      }

      try {
        const result = await translationSources[source](lang);
        if (result) {
          console.log(`✓ Loaded translations: ${source} (${lang})`);
        }
        return result;
      } catch (error) {
        // Silent fail for optional modules
        return null;
      }
    });

    const translationResults = await Promise.all(translationPromises);
    const merged = mergeTranslations(...translationResults);

    console.log(
      `Loaded ${
        translationResults.filter(Boolean).length
      } translation modules for ${lang}`
    );

    return merged;
  } catch (error) {
    console.error(`Failed to load translations for language "${lang}"`, error);
    throw error;
  }
};

/**
 * Register a new translation source dynamically
 * @param {string} name - Source name
 * @param {Function} loader - Function that loads the translations
 */
export const registerTranslationSource = (name, loader) => {
  translationSources[name] = loader;
  console.log(`✓ Registered translation source: ${name}`);
};

/**
 * Get all available translation sources
 */
export const getAvailableSources = () => Object.keys(translationSources);

/**
 * Preload all translations for better performance
 */
export const preloadAllTranslations = async (languages = ["en", "hi"]) => {
  const preloadPromises = languages.map((lang) => loadTranslations(lang));
  await Promise.all(preloadPromises);
  console.log("✓ All translations preloaded");
};
